/**
 * מנוע התחולה של הצ׳קליסט — כללים מפורשים בקוד, ללא AI (§11).
 *
 * כל שאלה שמופיעה או נעלמת מתעדת **סיבה בשפת אדם**. הדרישה הזו אינה
 * קוסמטית: בקר שרואה שאלה חייב לדעת מדוע היא כאן, ומי שקורא את הדוח
 * חייב לראות מה סוננה ולמה (MASTER §ט, RQ-E-series).
 */

import type { QuestionVersion, Site } from './types.ts';

export interface ApplicabilityInput {
  site: Site;
  hasOpenPreviousFindings: boolean;
}

export interface ApplicabilityResult {
  question: QuestionVersion;
  applicable: boolean;
  /** הסיבה מוצגת בממשק ובדוח גם כשהשאלה כן חלה. */
  reason: string;
}

const UNKNOWN_SHOWS_QUESTION =
  'המאפיין אינו ידוע — השאלה מוצגת כדי שלא ייווצר סינון על בסיס חוסר מידע';

/**
 * סף 15,000 מ״ר למינוי מנהל אתר — R-12 (תקנה 2(א)(2)), נוסח אומת.
 * הסף הוא מספר מהנוסח, ולא הערכה; ראו REGULATORY-MATRIX.
 */
export const SITE_MANAGER_AREA_THRESHOLD_SQM = 15_000;

export function evaluateApplicability(
  question: QuestionVersion,
  input: ApplicabilityInput,
): ApplicabilityResult {
  const { site, hasOpenPreviousFindings } = input;

  switch (question.code) {
    case 'Q-05': {
      if (site.permitAreaSqm === null) {
        return { question, applicable: true, reason: UNKNOWN_SHOWS_QUESTION };
      }
      const applicable = site.permitAreaSqm >= SITE_MANAGER_AREA_THRESHOLD_SQM;
      return {
        question,
        applicable,
        reason: applicable
          ? `שטח ההיתר ${formatSqm(site.permitAreaSqm)} מ״ר — בסף 15,000 מ״ר ומעלה`
          : `שטח ההיתר ${formatSqm(site.permitAreaSqm)} מ״ר — מתחת לסף 15,000 מ״ר`,
      };
    }

    case 'Q-09': {
      if (site.requiresSection192Notice === null) {
        return { question, applicable: true, reason: UNKNOWN_SHOWS_QUESTION };
      }
      return {
        question,
        applicable: site.requiresSection192Notice,
        reason: site.requiresSection192Notice
          ? 'האתר מחייב הודעה לפי סעיף 192'
          : 'האתר אינו מחייב הודעה לפי סעיף 192',
      };
    }

    case 'Q-10': {
      if (site.hasSiteManager === null) {
        return { question, applicable: true, reason: UNKNOWN_SHOWS_QUESTION };
      }
      return {
        question,
        applicable: site.hasSiteManager,
        reason: site.hasSiteManager
          ? 'קיים מנהל אתר — נדרשות בקרות כל 30 ימים'
          : 'לא מונה מנהל אתר באתר זה',
      };
    }

    case 'Q-11': {
      return {
        question,
        applicable: hasOpenPreviousFindings,
        reason: hasOpenPreviousFindings
          ? 'קיימים ממצאים פתוחים מביקור קודם'
          : 'אין ממצאים פתוחים מביקור קודם',
      };
    }

    default:
      return { question, applicable: true, reason: 'חלה בכל ביקור' };
  }
}

export function evaluateChecklist(
  questions: readonly QuestionVersion[],
  input: ApplicabilityInput,
): ApplicabilityResult[] {
  return questions.map((question) => evaluateApplicability(question, input));
}

function formatSqm(value: number): string {
  return value.toLocaleString('en-US');
}
